import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LoadingProps {
  size?: 'small' | 'medium' | 'large'
  color?: 'primary' | 'secondary' | 'white' | 'success' | 'warning' | 'error'
  className?: string
  showProgressBar?: boolean
  progress?: number
  progressText?: string
  fileName?: string
  showPercentage?: boolean
  showETA?: boolean
  startTime?: number
  animate?: boolean
  minimal?: boolean  // New: minimal mode - only bar and percentage
}

const Loading: React.FC<LoadingProps> = ({
  size = 'medium',
  color = 'primary',
  className = '',
  showProgressBar = false,
  progress = 0,
  progressText = '',
  fileName = '',
  showPercentage = true,
  showETA = false,
  startTime,
  animate = true,
  minimal = false
}) => {
  const { t } = useTranslation('common');
  const [estimatedTimeLeft, setEstimatedTimeLeft] = useState<string>('');
  
  const sizeClasses = {
    small: 'w-6 h-6',
    medium: 'w-10 h-10',
    large: 'w-16 h-16'
  }

  const colorClasses = {
    primary: 'text-primary',
    secondary: 'text-muted-foreground',
    white: 'text-white',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600'
  }

  const progressBarColors = {
    primary: 'bg-primary',
    secondary: 'bg-muted-foreground',
    white: 'bg-white',
    success: 'bg-green-600',
    warning: 'bg-yellow-600',
    error: 'bg-red-600'
  }

  // Calculate ETA
  useEffect(() => {
    if (showETA && startTime && progress > 0 && progress < 100) {
      const elapsed = Date.now() - startTime;
      const estimatedTotal = elapsed / (progress / 100);
      const remaining = estimatedTotal - elapsed;
      
      if (remaining > 0) {
        const seconds = Math.floor(remaining / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        let etaText = '';
        if (hours > 0) {
          etaText = `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
          etaText = `${minutes}m ${seconds % 60}s`;
        } else {
          etaText = `${seconds}s`;
        }
        
        setEstimatedTimeLeft(etaText);
      }
    }
  }, [progress, startTime, showETA]);

  if (showProgressBar) {
    // Minimal mode - only progress bar and percentage
    if (minimal) {
      return (
        <div className={`flex flex-col items-center justify-center ${className}`}>
          <div className="w-full max-w-md">
            {/* Progress Bar with Percentage */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1 mr-4">
                <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                  <div
                    className={`${progressBarColors[color]} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                  />
                </div>
              </div>
              {showPercentage && (
                <span className="text-sm font-semibold text-foreground tabular-nums min-w-[3rem] text-right">
                  {Math.round(progress)}%
                </span>
              )}
            </div>
          </div>
        </div>
      )
    }

    // Full mode - original design
    return (
      <div className={`flex flex-col items-center justify-center space-y-4 p-6 ${className}`}>
        {/* Enhanced Progress Section */}
        <div className="w-full max-w-md">
          {/* Header with spinner and text */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {/* Animated spinner */}
              <div className="inline-flex items-center justify-center" role="status">
                <svg
                  className={`${animate ? 'animate-spin' : ''} ${sizeClasses.small} ${colorClasses[color]}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-foreground">
                {progressText || t('loading.processing')}
              </span>
            </div>
            {showPercentage && (
              <span className="text-sm font-semibold text-foreground">
                {Math.round(progress)}%
              </span>
            )}
          </div>

          {/* Enhanced Progress Bar */}
          <div className="relative">
            <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
              <div
                className={`${progressBarColors[color]} h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden`}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              >
                {/* Animated stripes for active progress */}
                {animate && progress < 100 && (
                  <div className="absolute inset-0 bg-white bg-opacity-20 animate-pulse" />
                )}
              </div>
            </div>

            {/* Progress indicator dot */}
            {progress > 0 && progress < 100 && (
              <div
                className={`absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 ${progressBarColors[color]} rounded-full shadow-md transition-all duration-500`}
                style={{ left: `${Math.min(100, Math.max(0, progress))}%` }}
              >
                <div className="absolute inset-0 rounded-full bg-white opacity-30 animate-ping" />
              </div>
            )}
          </div>

          {/* Additional info section */}
          <div className="mt-3 space-y-1">
            {fileName && (
              <div className="text-xs text-muted-foreground truncate">
                <span className="font-medium">{t('loading.file')}:</span> {fileName}
              </div>
            )}

            {showETA && estimatedTimeLeft && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">{t('loading.eta')}:</span> {estimatedTimeLeft}
              </div>
            )}

            {/* Speed indicator for large files */}
            {progress > 5 && progress < 95 && (
              <div className="flex items-center space-x-1">
                <div className="flex space-x-0.5">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-1 h-${i + 1} ${progressBarColors[color]} rounded-full opacity-${(i + 1) * 20}`}
                      style={{
                        animation: animate ? `pulse ${1.5 - i * 0.2}s ease-in-out infinite` : 'none'
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{t('loading.processing')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`inline-flex items-center justify-center ${className}`} role="status">
      <div className="relative">
        <svg 
          className={`${animate ? 'animate-spin' : ''} ${sizeClasses[size]} ${colorClasses[color]}`}
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {/* Pulse effect for better visibility */}
        {animate && (
          <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full ${colorClasses[color]} opacity-20 animate-ping`} />
        )}
      </div>
      <span className="sr-only">{t('loading.loading')}</span>
    </div>
  )
}

export default Loading
